import type React from "react";

import { Button } from "@bcc/components/ui/button";
import { Calendar } from "@bcc/components/ui/calendar";
import { Label } from "@bcc/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@bcc/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@bcc/components/ui/select";
import { Textarea } from "@bcc/components/ui/textarea";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { setChangeAvailabilityPopoverTestId } from "./change-availability-popover.utils";

type Variant = {
  id: string;
  size: string;
  status: "published" | "out_of_stock" | "rejected" | "draft";
};

interface ChangeAvailabilityPopoverProps {
  variant: Variant;
  children: React.ReactNode;
  testId?: string;
}

export function ChangeAvailabilityPopover({
  variant,
  children,
  testId,
}: ChangeAvailabilityPopoverProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(variant.status);
  const [effectiveDate, setEffectiveDate] = useState<Date>();
  const [notes, setNotes] = useState("");

  const handleSave = () => {
    console.log("[v0] Saving availability change:", {
      status,
      effectiveDate,
      notes,
    });
    // API call would go here
    setOpen(false);
  };

  const isSunday = (date: Date) => {
    return date.getDay() === 0;
  };

  const disableNonSundays = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Disable if date is in the past (before today)
    if (date < today) {
      return true;
    }

    // Disable if date is not a Sunday
    if (!isSunday(date)) {
      return true;
    }

    return false;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        testId={testId ?? setChangeAvailabilityPopoverTestId("content")}
        className="w-80"
        align="start"
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <h4 className="font-medium text-sm">
              {t("products.changeAvailability.title")}
            </h4>
            <p className="text-xs text-muted-foreground">
              {t("products.changeAvailability.description", {
                size: variant.size,
              })}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="status" className="text-xs">
              {t("products.changeAvailability.status")}
            </Label>
            <Select
              value={status}
              onValueChange={(value: any) => setStatus(value)}
            >
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="published">
                  {t("products.changeAvailability.statusOptions.published")}
                </SelectItem>
                <SelectItem value="out_of_stock">
                  {t("products.changeAvailability.statusOptions.outOfStock")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">
              {t("products.changeAvailability.effectiveDate")}
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal bg-transparent"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {effectiveDate
                    ? format(effectiveDate, "PPP")
                    : t("products.changeAvailability.selectDate")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={effectiveDate}
                  onSelect={setEffectiveDate}
                  disabled={disableNonSundays}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <p className="text-xs text-muted-foreground">
              {t("products.changeAvailability.sundaysOnly")}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes" className="text-xs">
              {t("products.changeAvailability.notesOptional")}
            </Label>
            <Textarea
              id="notes"
              placeholder={t("products.changeAvailability.notesPlaceholder")}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOpen(false)}
              className="flex-1"
            >
              {t("common.cancel")}
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              className="flex-1 bg-primary"
            >
              {t("products.changeAvailability.submitForApproval")}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
